import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Send, Printer, MonitorPlay, FileDown, MoreHorizontal, Plus, Trash2, ArrowDown, Copy, FileText, Presentation, BookOpen, Timer, ScanLine } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { SegmentCard } from "@/components/SegmentCard";
import { InlineCategoryPicker } from "@/components/InlineCategoryPicker";
import { ShareModal } from "@/components/ShareModal";
import { PresentMode } from "@/components/PresentMode";
import { SlideFilmstrip } from "@/components/SlideFilmstrip";
import { RehearsalPanel } from "@/components/RehearsalPanel";
import { ProjectorCheck } from "@/components/ProjectorCheck";
import { ScriptureDialog } from "@/components/ScriptureDialog";
import { DEFAULT_FONT, DEFAULT_FONT_PT, DEFAULT_TRANSITION_MS } from "@/lib/slides";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { computeTimeRanges, totalDuration } from "@/lib/session";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAccessibility } from "@/contexts/AccessibilityContext";

function uuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return "seg-" + Math.random().toString(36).slice(2) + Date.now();
}

export default function SessionPlanner() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { textScale, textScaleValue, setTextScale } = useAccessibility();
  const [session, setSession] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [insertIndex, setInsertIndex] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [presenting, setPresenting] = useState(false);
  const [presentStart, setPresentStart] = useState(0);
  const [rehearsing, setRehearsing] = useState(false);
  const [rehearsalOpen, setRehearsalOpen] = useState(false);
  const [rehearsalResult, setRehearsalResult] = useState(null);
  const [projectorOpen, setProjectorOpen] = useState(false);
  const [scriptureOpen, setScriptureOpen] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const saveTimer = useRef(null);

  useEffect(() => {
    const init = async () => {
      if (sessionId === "new") {
        const { data } = await api.post("/sessions");
        navigate(`/planner/${data.id}`, { replace: true });
        return;
      }
      const { data } = await api.get(`/sessions/${sessionId}`);
      setSession(data);
    };
    init();
  }, [sessionId, navigate]);

  const persist = useCallback((next) => {
    setSaving(true);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      await api.put(`/sessions/${next.id}`, {
        title: next.title,
        subtitle: next.subtitle,
        start_time: next.start_time,
        session_date: next.session_date,
        slide_font: next.slide_font,
        slide_font_size: next.slide_font_size,
        slide_transition_ms: next.slide_transition_ms,
        segments: next.segments,
      });
      setSaving(false);
    }, 600);
  }, []);

  const updateSession = (changes) => {
    setSession((prev) => {
      const next = { ...prev, ...changes };
      persist(next);
      return next;
    });
  };

  const updateSegment = (id, changes) => {
    setSession((prev) => {
      const segments = prev.segments.map((s) => (s.id === id ? { ...s, ...changes } : s));
      const next = { ...prev, segments };
      persist(next);
      return next;
    });
  };

  const deleteSegment = (id) => {
    setSession((prev) => {
      const segments = prev.segments.filter((s) => s.id !== id);
      const next = { ...prev, segments };
      persist(next);
      return next;
    });
  };

  const duplicateSegment = (id) => {
    setSession((prev) => {
      const idx = prev.segments.findIndex((s) => s.id === id);
      if (idx === -1) return prev;
      const clone = { ...prev.segments[idx], id: uuid() };
      const segments = [...prev.segments];
      segments.splice(idx + 1, 0, clone);
      const next = { ...prev, segments };
      persist(next);
      return next;
    });
    toast.success("Segment duplicated");
  };

  const openPicker = (index) => {
    if (pickerOpen && insertIndex === index) {
      setPickerOpen(false);
      return;
    }
    setInsertIndex(index);
    setPickerOpen(true);
  };

  const handleSelectItem = (item) => {
    const newSegment = {
      id: uuid(),
      category: item.category,
      title: item.title,
      body: item.body || "",
      details: "",
      duration_minutes: item.default_duration_minutes || 5,
    };
    setSession((prev) => {
      const segments = [...prev.segments];
      const idx = insertIndex === null ? segments.length : insertIndex;
      segments.splice(idx, 0, newSegment);
      const next = { ...prev, segments };
      persist(next);
      return next;
    });
    setPickerOpen(false);
  };

  const handleDragStart = (e, index) => setDragIndex(index);  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (e, index) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    setSession((prev) => {
      const segments = [...prev.segments];
      const [moved] = segments.splice(dragIndex, 1);
      segments.splice(index, 0, moved);
      const next = { ...prev, segments };
      persist(next);
      return next;
    });
    setDragIndex(null);
  };

  const moveSegment = (index, direction) => {
    setSession((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.segments.length) return prev;
      const segments = [...prev.segments];
      [segments[index], segments[target]] = [segments[target], segments[index]];
      const next = { ...prev, segments };
      persist(next);
      return next;
    });
  };
  const moveSegmentUp = (index) => moveSegment(index, -1);
  const moveSegmentDown = (index) => moveSegment(index, 1);

  const handleInsertScripture = ({ title, body }) => {
    const newSegment = {
      id: uuid(),
      category: "Prayer",
      title,
      body,
      details: "",
      duration_minutes: 5,
    };
    setSession((prev) => {
      const next = { ...prev, segments: [...prev.segments, newSegment] };
      persist(next);
      return next;
    });
    toast.success("Scripture added to the session");
  };

  const startWalkthrough = () => {
    setRehearsalResult(null);
    setRehearsalOpen(false);
    setPresentStart(0);
    setRehearsing(true);
    setPresenting(true);
  };

  const finishWalkthrough = (timings) => {
    setRehearsalResult({ timings });
    setRehearsing(false);
    setRehearsalOpen(true);
  };

  const handleExport = async (type) => {
    try {
      const { data } = await api.get(`/sessions/${session.id}/export/${type}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${session.title || "session"}.${type}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error("Export failed. Please try again.");
    }
  };

  const handleDeleteSession = async () => {
    await api.delete(`/sessions/${session.id}`);
    navigate("/sessions");
  };

  const handleSaveAsTemplate = async () => {
    try {
      await api.post(`/sessions/${session.id}/save-as-template`);
      toast.success("Saved as a reusable template. Find it under My Sessions > Templates");
    } catch {
      toast.error("Could not save as template. Please try again.");
    }
  };

  if (!session) {
    return (
      <div className="min-h-screen bg-[var(--brand-bg)] dark:bg-[var(--brand-bg-dark)] flex items-center justify-center">
        <p className="font-display italic text-lg text-muted-foreground">Loading your session...</p>
      </div>
    );
  }

  const ranges = computeTimeRanges(session.start_time, session.segments);
  const total = totalDuration(session.segments);
  const isOwner = session.is_owner !== false;

  return (
    <div className="min-h-screen bg-[var(--brand-bg)] dark:bg-[var(--brand-bg-dark)]">
      <Navbar />
      <header className="bg-gradient-to-r from-[var(--brand-navy)] to-[var(--brand-navy-hover)] text-amber-50 px-6 py-8">
        <div className="max-w-5xl mx-auto flex items-start justify-between">
          <div>
            <h1 className="font-sacred text-3xl sm:text-4xl">Prayer Session Planner</h1>
            <p className="font-display italic text-amber-100/80 mt-1">Formulate, order, and time your sacred intercession session</p>
          </div>
          <p className="text-xs text-amber-200/70 no-print" data-testid="autosave-indicator">{saving ? "Saving..." : "All changes saved"}</p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 -mt-2">
        <div className="bg-white dark:bg-[var(--brand-card-dark)] border-x border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] rounded-t-lg px-4 sm:px-6 pt-3 flex items-center justify-between no-print">
          <div className="border-b-2 border-[var(--brand-navy)] dark:border-amber-400 font-semibold text-sm pb-2 px-1">Agenda</div>
          <div className="flex items-center gap-1.5 pb-2">
            <div className="flex items-center gap-0.5 border rounded-full p-0.5 mr-1" role="group" aria-label="Text size" data-testid="text-size-controls">
              <Button
                size="icon"
                variant={textScale === "normal" ? "secondary" : "ghost"}
                className="h-7 w-7 rounded-full text-xs"
                onClick={() => setTextScale("normal")}
                title="Normal text size"
                data-testid="text-size-normal-button"
              >
                A
              </Button>
              <Button
                size="icon"
                variant={textScale === "large" ? "secondary" : "ghost"}
                className="h-7 w-7 rounded-full text-sm"
                onClick={() => setTextScale("large")}
                title="Large text size"
                data-testid="text-size-large-button"
              >
                A
              </Button>
              <Button
                size="icon"
                variant={textScale === "xlarge" ? "secondary" : "ghost"}
                className="h-7 w-7 rounded-full text-base"
                onClick={() => setTextScale("xlarge")}
                title="Extra large text size"
                data-testid="text-size-xlarge-button"
              >
                A
              </Button>
            </div>
            {isOwner && (
              <Button size="icon" variant="outline" className="h-8 w-8 rounded-full" onClick={() => setShareOpen(true)} data-testid="action-share-button">
                <Send className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button size="icon" variant="outline" className="h-8 w-8 rounded-full" onClick={() => window.print()} data-testid="action-print-button">
              <Printer className="h-3.5 w-3.5" />
            </Button>
            <Button size="icon" variant="outline" className="h-8 w-8 rounded-full" onClick={() => setScriptureOpen(true)} title="Add scripture" data-testid="action-scripture-button">
              <BookOpen className="h-3.5 w-3.5" />
            </Button>
            <Button size="icon" variant="outline" className="h-8 w-8 rounded-full" onClick={() => { setPresentStart(0); setRehearsing(false); setPresenting(true); }} title="Present now" data-testid="action-present-button">
              <MonitorPlay className="h-3.5 w-3.5" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon" variant="outline" className="h-8 w-8 rounded-full" title="Download" data-testid="action-download-button">
                  <FileDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport("pdf")} data-testid="action-export-pdf-button">
                  <FileText className="h-3.5 w-3.5 mr-2" /> Download PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("pptx")} data-testid="action-export-pptx-button">
                  <Presentation className="h-3.5 w-3.5 mr-2" /> Download PowerPoint
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon" variant="outline" className="h-8 w-8 rounded-full" data-testid="action-more-button">
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => { setRehearsalResult(null); setRehearsalOpen(true); }} data-testid="action-rehearse-session">
                  <Timer className="h-3.5 w-3.5 mr-2" /> Rehearse session
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setProjectorOpen(true)} data-testid="action-projector-check">
                  <ScanLine className="h-3.5 w-3.5 mr-2" /> Projector check
                </DropdownMenuItem>
                {isOwner && (
                  <DropdownMenuItem onClick={handleSaveAsTemplate} data-testid="action-save-as-template">
                    <Copy className="h-3.5 w-3.5 mr-2" /> Save as template
                  </DropdownMenuItem>
                )}
                {isOwner && (
                  <DropdownMenuItem className="text-[var(--brand-crimson)]" onClick={handleDeleteSession} data-testid="action-delete-session">
                    <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete session
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="bg-[#F5F3EE] dark:bg-[var(--brand-bg-dark)] border-x border-b border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] rounded-b-lg px-4 sm:px-6 py-6 sm:py-8 space-y-5" style={{ zoom: textScaleValue }} data-testid="agenda-content-scaled">
          <div className="space-y-2">
            <Input
              value={session.title}
              onChange={(e) => updateSession({ title: e.target.value })}
              className="h-auto text-2xl sm:text-3xl md:text-3xl font-display font-semibold bg-white dark:bg-white/5 border border-stone-400 dark:border-[var(--brand-border-dark)] rounded-md px-3 py-2 focus-visible:ring-1 focus-visible:ring-[var(--brand-crimson)] focus-visible:border-[var(--brand-crimson)]"
              data-testid="session-title-input"
            />
            <Input
              value={session.subtitle}
              onChange={(e) => updateSession({ subtitle: e.target.value })}
              className="h-auto text-sm md:text-sm text-muted-foreground bg-white dark:bg-white/5 border border-stone-400 dark:border-[var(--brand-border-dark)] rounded-md px-3 py-1.5 focus-visible:ring-1 focus-visible:ring-[var(--brand-crimson)] focus-visible:border-[var(--brand-crimson)]"
              data-testid="session-subtitle-input"
            />
          </div>

          {session.segments.length > 0 && (
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2 text-muted-foreground">
                Start
                <Input type="time" value={session.start_time} onChange={(e) => updateSession({ start_time: e.target.value })} className="w-28 h-8" data-testid="session-start-time-input" />
              </label>
              <span className="font-mono text-xs px-2.5 py-1 rounded-full bg-amber-100 dark:bg-amber-950/40 text-amber-900 dark:text-amber-300 border border-[var(--brand-gold)]" data-testid="session-total-duration-badge">
                {total} min total
              </span>
            </div>
          )}

          <AddDivider onClick={() => openPicker(0)} testId="add-divider-top" />
          {pickerOpen && insertIndex === 0 && (
            <InlineCategoryPicker onSelect={handleSelectItem} onClose={() => setPickerOpen(false)} />
          )}

          {session.segments.map((seg, idx) => (
            <div key={seg.id}>
              <SegmentCard
                segment={seg}
                index={idx}
                total={session.segments.length}
                start={ranges[idx].start}
                end={ranges[idx].end}
                onUpdate={updateSegment}
                onDelete={deleteSegment}
                onDuplicate={duplicateSegment}
                onMoveUp={moveSegmentUp}
                onMoveDown={moveSegmentDown}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                dragging={dragIndex === idx}
              />
              <AddDivider onClick={() => openPicker(idx + 1)} testId={`add-divider-${idx}`} />
              {pickerOpen && insertIndex === idx + 1 && (
                <InlineCategoryPicker onSelect={handleSelectItem} onClose={() => setPickerOpen(false)} />
              )}
            </div>
          ))}

          {session.segments.length === 0 && (
            <div className="text-center py-4" data-testid="empty-state-prompt">
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Start by adding steps to your agenda</p>
              <ArrowDown className="h-5 w-5 mx-auto text-muted-foreground/60" />
            </div>
          )}

          <Button onClick={() => openPicker(null)} className="w-full h-14 bg-white dark:bg-[var(--brand-card-dark)] border border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] text-[var(--brand-crimson)] hover:bg-[var(--brand-bg)] rounded-lg" data-testid="add-segment-bottom-button">
            <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[var(--brand-crimson)] text-white mr-2">
              <Plus className="h-4 w-4" />
            </span>
            Add
          </Button>
          {pickerOpen && insertIndex === null && (
            <InlineCategoryPicker onSelect={handleSelectItem} onClose={() => setPickerOpen(false)} />
          )}

          <SlideFilmstrip
            segments={session.segments}
            font={session.slide_font || DEFAULT_FONT}
            fontSize={session.slide_font_size || DEFAULT_FONT_PT}
            transitionMs={session.slide_transition_ms || DEFAULT_TRANSITION_MS}
            onStyleChange={isOwner ? updateSession : undefined}
            onPresentFrom={(i) => {
              setPresentStart(i);
              setRehearsing(false);
              setPresenting(true);
            }}
          />
        </div>
      </main>

      {presenting && (
        <PresentMode
          session={session}
          startIndex={presentStart}
          rehearsal={rehearsing}
          canShare={isOwner}
          onRehearsalEnd={finishWalkthrough}
          onClose={() => { setPresenting(false); setRehearsing(false); }}
        />
      )}
      {projectorOpen && (
        <ProjectorCheck
          font={session.slide_font || DEFAULT_FONT}
          fontSize={session.slide_font_size || DEFAULT_FONT_PT}
          onClose={() => setProjectorOpen(false)}
        />
      )}
      <RehearsalPanel
        open={rehearsalOpen}
        onClose={() => setRehearsalOpen(false)}
        session={session}
        result={rehearsalResult}
        onStartWalkthrough={startWalkthrough}
      />
      <ScriptureDialog open={scriptureOpen} onClose={() => setScriptureOpen(false)} onInsert={handleInsertScripture} />
      {isOwner && <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} session={session} onShareChange={setSession} />}
    </div>
  );
}

function AddDivider({ onClick, testId }) {
  return (
    <div className="relative h-4 group no-print cursor-pointer" onClick={onClick} data-testid={testId}>
      <div className="absolute inset-x-0 top-1/2 h-px bg-foreground/25 group-hover:bg-transparent transition-colors" />
      <button
        onClick={onClick}
        className="absolute left-1/2 -translate-x-1/2 -top-2.5 opacity-0 group-hover:opacity-100 transition-opacity bg-[var(--brand-navy)] text-white text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1 shadow-md hover:bg-[var(--brand-navy-hover)]"
      >
        <Plus className="h-3 w-3" /> Add
      </button>
    </div>
  );
}
